import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Collapse,
  Divider,
  Tooltip,
  alpha,
  CircularProgress
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';
import { useSectorName } from '../../hooks/useSectorName';

const AIInsightsSection = ({
  aiInsights,
  aiLoading,
  valuationFilter,
  getRecommendationColor = (recommendation) => {
    if (recommendation === 'STRONG_BUY') return '#11998e';
    if (recommendation === 'BUY') return '#667eea';
    if (recommendation === 'HOLD') return '#6366f1';
    if (recommendation === 'SELL') return '#ff6b6b';
    if (recommendation === 'STRONG_SELL') return '#d32f2f';
    return '#999';
  },
  stripEmojis = (text) => {
    if (!text || typeof text !== 'string') return text;
    try {
      return text.replace(/[\u{1F300}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '').trim();
    } catch (e) {
      return text;
    }
  }
}) => {
  const { t } = useTranslation();
  const { getSectorName } = useSectorName();
  const [expandedSector, setExpandedSector] = useState(null);

  const getRecommendationLabel = (recommendation) => {
    switch (recommendation) {
      case 'STRONG_BUY':
        return t('dashboard.recommendations.strongBuy');
      case 'BUY':
        return t('dashboard.recommendations.buy');
      case 'HOLD':
        return t('dashboard.recommendations.hold');
      case 'SELL':
        return t('dashboard.recommendations.sell');
      case 'STRONG_SELL':
        return t('dashboard.recommendations.strongSell');
      default:
        return recommendation;
    }
  };

  const getRankColor = (rankIndex) => {
    if (rankIndex === 0) return '#D4AF37'; // gold
    if (rankIndex === 1) return '#C0C0C0'; // silver
    if (rankIndex === 2) return '#CD7F32'; // bronze
    return '#667eea'; // subtle blue for others
  };

  if (!aiInsights || aiInsights.count === 0) return null;

  return (
    <Collapse in={!valuationFilter && aiInsights && aiInsights.count > 0} timeout={400}>
      <Card
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha('#e2e8f0', 1),
          overflow: 'hidden',
          boxShadow: `0 4px 20px ${alpha('#667eea', 0.06)}`
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha('#667eea', 0.03)} 0%, ${alpha('#764ba2', 0.05)} 100%)`,
            borderBottom: '1px solid',
            borderColor: alpha('#e2e8f0', 1),
            p: 3,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
              >
                <SmartToyIcon sx={{ fontSize: '1.75rem', color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', fontSize: '1.125rem', mb: 0.5 }}>
                  {t('vnstock.stocks.aiInsights.title', { count: aiInsights.count })}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem', fontWeight: 500 }}>
                  {t('vnstock.stocks.aiInsights.subtitle')}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={t('vnstock.stocks.aiInsights.beta')}
              size="small"
              sx={{
                bgcolor: alpha('#667eea', 0.1),
                color: '#5a67d8',
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 24,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                border: `1.5px solid ${alpha('#667eea', 0.25)}`
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 3, bgcolor: '#fafafa' }}>
          {aiLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={48} sx={{ color: '#ff9800' }} />
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {Object.entries(aiInsights.insights || {}).map(([sectorName, insights], index) => {
                const sectorObj = {
                  sector_name: sectorName,
                  sector_code: insights.sector_code,
                  name_vi: insights.name_vi
                };

                return (
                  <Card
                    key={sectorName}
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: alpha('#e2e8f0', 1),
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      bgcolor: 'white',
                      '&:hover': {
                        borderColor: alpha(getRecommendationColor(insights.recommendation), 0.3),
                        boxShadow: `0 4px 20px ${alpha(getRecommendationColor(insights.recommendation), 0.08)}`,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        p: 2.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: alpha(getRecommendationColor(insights.recommendation), 0.02),
                        '&:hover': {
                          bgcolor: alpha(getRecommendationColor(insights.recommendation), 0.04)
                        }
                      }}
                      onClick={() => setExpandedSector(expandedSector === sectorName ? null : sectorName)}
                    >
                      <Box display="flex" alignItems="center" gap={2.5}>
                        {/* Ranking Badge */}
                        <Box
                          sx={{
                            borderRadius: '12px',
                            width: 44,
                            height: 44,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none'
                          }}
                        >
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: getRankColor(index),
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.95rem'
                          }}>
                            {index + 1}
                          </Box>
                        </Box>

                        {/* Sector Name */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: '#1a202c',
                                fontSize: '1rem',
                                letterSpacing: '-0.02em',
                                lineHeight: 1.3,
                              }}
                            >
                              {getSectorName(sectorObj)}
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getRecommendationColor(insights.recommendation) }} />
                                  <Box sx={{ fontWeight: 700, fontSize: '0.68rem', color: getRecommendationColor(insights.recommendation) }}>
                                    {getRecommendationLabel(insights.recommendation)}
                                  </Box>
                                </Box>
                              }
                              sx={{
                                ml: 0.5,
                                height: 22,
                                px: 0.75,
                                borderRadius: 16,
                                bgcolor: alpha(getRecommendationColor(insights.recommendation), 0.08),
                                color: getRecommendationColor(insights.recommendation),
                                fontWeight: 700,
                                fontSize: '0.68rem',
                                textTransform: 'none',
                                border: `1px solid ${alpha(getRecommendationColor(insights.recommendation), 0.14)}`,
                                boxShadow: 'none',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          >
                            {insights.sector_code}
                          </Typography>
                        </Box>

                        {/* Metrics Group */}
                        <Box display="flex" alignItems="center" gap={2}>
                          {/* Confidence */}
                          <Tooltip
                            title={t('vnstock.stocks.aiInsights.confidence')}
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.75,
                                borderRadius: '12px',
                                bgcolor: 'white',
                                border: '1px solid',
                                borderColor: alpha('#3b82f6', 0.2),
                                boxShadow: `0 2px 8px ${alpha('#3b82f6', 0.1)}`,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                minWidth: 70,
                                '&:hover': {
                                  borderColor: alpha('#3b82f6', 0.4),
                                  boxShadow: `0 4px 16px ${alpha('#3b82f6', 0.2)}`,
                                  transform: 'translateY(-1px)'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <BarChartIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: '1rem',
                                    color: '#1e40af',
                                    lineHeight: 1
                                  }}
                                >
                                  {(insights.confidence * 100).toFixed(0)}%
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 4,
                                  bgcolor: alpha('#3b82f6', 0.15),
                                  borderRadius: 2,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${(insights.confidence * 100)}%`,
                                    height: '100%',
                                    background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)',
                                    borderRadius: 2,
                                    transition: 'width 0.5s ease'
                                  }}
                                />
                              </Box>
                            </Box>
                          </Tooltip>

                          {/* Valuation Score */}
                          <Tooltip
                            title={t('vnstock.stocks.aiInsights.valScore')}
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: '12px',
                                bgcolor: 'white',
                                border: '1px solid',
                                borderColor: insights.valuation_analysis?.val_score >= 60
                                  ? alpha('#10b981', 0.25)
                                  : insights.valuation_analysis?.val_score >= 40
                                    ? alpha('#f59e0b', 0.25)
                                    : alpha('#ef4444', 0.25),
                                boxShadow: insights.valuation_analysis?.val_score >= 60
                                  ? `0 2px 8px ${alpha('#10b981', 0.15)}`
                                  : insights.valuation_analysis?.val_score >= 40
                                    ? `0 2px 8px ${alpha('#f59e0b', 0.15)}`
                                    : `0 2px 8px ${alpha('#ef4444', 0.15)}`,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: insights.valuation_analysis?.val_score >= 60
                                    ? `0 4px 16px ${alpha('#10b981', 0.25)}`
                                    : insights.valuation_analysis?.val_score >= 40
                                      ? `0 4px 16px ${alpha('#f59e0b', 0.25)}`
                                      : `0 4px 16px ${alpha('#ef4444', 0.25)}`,
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: insights.valuation_analysis?.val_score >= 60
                                    ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
                                    : insights.valuation_analysis?.val_score >= 40
                                      ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                                      : 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
                                  boxShadow: insights.valuation_analysis?.val_score >= 60
                                    ? `0 2px 8px ${alpha('#10b981', 0.4)}`
                                    : insights.valuation_analysis?.val_score >= 40
                                      ? `0 2px 8px ${alpha('#f59e0b', 0.4)}`
                                      : `0 2px 8px ${alpha('#ef4444', 0.4)}`,
                                }}
                              >
                                {insights.valuation_analysis?.val_score >= 60
                                  ? <TrendingUpIcon sx={{ fontSize: 16, color: 'white' }} />
                                  : insights.valuation_analysis?.val_score >= 40
                                    ? <ShowChartIcon sx={{ fontSize: 16, color: 'white' }} />
                                    : <TrendingUpIcon sx={{ fontSize: 16, color: 'white', transform: 'rotate(180deg)' }} />
                                }
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  fontSize: '1rem',
                                  color: insights.valuation_analysis?.val_score >= 60
                                    ? '#047857'
                                    : insights.valuation_analysis?.val_score >= 40
                                      ? '#b45309'
                                      : '#dc2626',
                                  lineHeight: 1
                                }}
                              >
                                {insights.valuation_analysis?.val_score?.toFixed(1) || 'N/A'}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>

                        {/* Expand Icon */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            bgcolor: alpha('#64748b', 0.08),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha('#64748b', 0.15)
                            }
                          }}
                        >
                          <ExpandMoreIcon
                            sx={{
                              color: '#475569',
                              transform: expandedSector === sectorName ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              fontSize: '1.5rem'
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Expanded Content */}
                    <Collapse in={expandedSector === sectorName}>
                      <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid', borderColor: alpha('#e2e8f0', 1) }}>
                        {/* Reasoning */}
                        <Box mb={2.5}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2d3748', mb: 1 }}>
                            {t('vnstock.stocks.aiInsights.reasoning')}
                          </Typography>
                          <Box display="flex" flexDirection="column" gap={1.25}>
                            {insights.reasoning?.map((reason, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  p: 1.25,
                                  borderRadius: 2,
                                  bgcolor: alpha('#f8fafc', 1),
                                  borderLeft: `4px solid ${alpha(getRecommendationColor(insights.recommendation), 0.14)}`
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#374151',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.5
                                  }}
                                >
                                  {stripEmojis(reason)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2, borderColor: alpha('#e2e8f0', 1) }} />

                        {/* Catalysts & Risks */}
                        <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
                          {/* Catalysts */}
                          {insights.catalysts && insights.catalysts.length > 0 && (
                            <Box
                              flex={1}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: alpha('#f0fdf4', 1),
                                borderLeft: `4px solid ${alpha('#10b981', 0.18)}`
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                                <CheckCircleIcon sx={{ color: '#10b981', fontSize: '1rem' }} />
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#047857' }}>
                                  {t('vnstock.stocks.aiInsights.catalysts')}
                                </Typography>
                              </Box>
                              <Box>
                                {insights.catalysts.map((catalyst, idx) => (
                                  <Typography
                                    key={idx}
                                    variant="body2"
                                    sx={{
                                      color: '#374151',
                                      mb: 0.75,
                                      pl: 1.25,
                                      fontSize: '0.95rem',
                                      lineHeight: 1.4
                                    }}
                                  >
                                    • {stripEmojis(catalyst)}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Risks */}
                          {insights.risks && insights.risks.length > 0 && (
                            <Box
                              flex={1}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: alpha('#fff7ed', 1),
                                borderLeft: `4px solid ${alpha('#fb923c', 0.18)}`
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                                <WarningIcon sx={{ color: '#fb923c', fontSize: '1rem' }} />
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#b45309' }}>
                                  {t('vnstock.stocks.aiInsights.risks')}
                                </Typography>
                              </Box>
                              <Box>
                                {insights.risks.map((risk, idx) => (
                                  <Typography
                                    key={idx}
                                    variant="body2"
                                    sx={{
                                      color: '#374151',
                                      mb: 0.75,
                                      pl: 1.25,
                                      fontSize: '0.95rem',
                                      lineHeight: 1.4
                                    }}
                                  >
                                    • {stripEmojis(risk)}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Collapse>
                  </Card>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    </Collapse>
  );
};

export default AIInsightsSection;